import { modals } from "@mantine/modals";
import DataTable from "../components/common/DataTable";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Divider, Modal, Paper, PasswordInput, SimpleGrid, Switch, Text, TextInput, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";

import { addAdmin, deleteAdmin, getAllAdmins, updateAdmin } from "../utils/api";

import { useEffect, useState } from "react";
import useStore from "../utils/store";

const Admin = () => {

  const user = useStore((state) => state.user);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admins"],
    queryFn: () => (user?.getAdmin ? getAllAdmins() : []),
  });

  const values = {
    getDashboard: "View Dashboard",
    activeSubscriptionUser: "Active Subscribers",
    addAdmin: "Create Admin",
    updateAdmin: "Edit Admin",
    deleteAdmin: "Remove Admin",
    getAdmin: "View Admins",
    adminValues: "Admin Settings",
    addBanner: "Create Banner",
    updateBanner: "Edit Banner",
    deleteBanner: "Remove Banner",
    getAllBanners: "View All Banners",
    addComingSoon: "Add Coming Soon Content",
    updateComingSoon: "Edit Coming Soon Content",
    deleteComingSoon: "Remove Coming Soon Content",
    getAllComingSoon: "View Coming Soon Content",
    addDisableUser: "Disable User",
    updateDisableUser: "Edit Disabled User",
    deleteDisableUser: "Remove Disabled User",
    getAllDisableUsers: "View Disabled Users",
    addLanguage: "Add Language",
    updateLanguage: "Edit Language",
    deleteLanguage: "Remove Language",
    getAllLanguages: "View All Languages",
    addMovie: "Upload Movie",
    updateMovie: "Edit Movie",
    deleteMovie: "Remove Movie",
    getAllMovies: "View All Movies",
    addPoster: "Add Poster",
    updatePoster: "Edit Poster",
    deletePoster: "Remove Poster",
    getAllPosters: "View All Posters",
    addSeries: "Upload Series",
    updateSeries: "Edit Series",
    deleteSeries: "Remove Series",
    getAllSeries: "View All Series",
    addUser: "Create User",
    updateUser: "Edit User",
    deleteUser: "Remove User",
    getAllUsers: "View All Users",
    userSubscriptionHistory: "Subscription History",
    userNotifications: "Send User Notifications",
    addNotification: "Create Notification",
    getAllTransactions: "View Transactions",
    getAllGenres: "View Genres",
    addGenre: "Create Genre",
    editGenre: "Edit Genre",
    deleteGenre: "Remove Genre",
    getAllCreators: "View Partners",
    addCreators: "Add Partner",
    editCreators: "Edit Partner",
    deleteCreators: "Remove Partner",
    getVerifiedCreators: "Verified Partners",
    getNonVerifiedCreators: "Unverified Partners",
    getPendingSeries: "Pending Series",
    editPendingSeries: "Review Pending Series",
    deletePendingSeries: "Reject Pending Series",
    getPendingMovies: "Pending Movies",
    editPendingMovies: "Review Pending Movies",
    deletePendingMovies: "Reject Pending Movies",
    getRejectedSeries: "Rejected Series",
    editRejectedSeries: "Edit Rejected Series",
    deleteRejectedSeries: "Delete Rejected Series",
    getRejectedMovies: "Rejected Movies",
    editRejectedMovies: "Edit Rejected Movies",
    deleteRejectedMovies: "Delete Rejected Movies",
    getAllFinanceEmployees: "View Finance Team",
    addFinanceEmployee: "Add Finance Employee",
    editFinanceEmployee: "Edit Finance Employee",
    deleteFinanceEmployee: "Remove Finance Employee",
    getAllMarketingEmployees: "View Marketing Team",
    addMarketingEmployee: "Add Marketing Employee",
    editMarketingEmployee: "Edit Marketing Employee",
    deleteMarketingEmployee: "Remove Marketing Employee",
    getAllInvoices: "View Invoices",
    editInvoice: "Edit Invoice",
    isPublishAccess: "Publishing Permission",
  };


  const { mutate, isPending: isLoading2 } = useMutation({
    mutationFn: async (values) => await deleteAdmin(values),
    onSuccess: () => {
      notifications.show({ message: `Deleted`, color: "green" });
      refetch();
    },
  });

  const columns = [
    {
      title: "Actions",
      dataIndex: "action",
      render(value, record) {
        return (
          <div>
            {user?.updateAdmin && (
              <AddData
                update2={record}
                refech={refetch}
                values={Object.keys(values).map((key) => ({
                  key: key,
                  value: values?.[key],
                }))}
              />
            )}
            {user?.deleteAdmin && (
              <span onClick={() => modals.openConfirmModal({ title: "Delete the task", labels: { confirm: "Yes", cancel: "No" }, onConfirm: () => mutate(record._id) })}>
                <Button color="red" style={{ marginTop: 10 }} loading={isLoading2}>
                  Delete
                </Button>
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "Email",
      dataIndex: "email",
    },
  ];

  return (
    <div
      style={{
        padding: 24,
      }}
    >

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <Title order={3} style={{ marginBottom: 10 }}>
          Admin
        </Title>
        {user?.addAdmin && (
          <AddData
            refech={refetch}
            values={Object.keys(values).map((key) => ({
              key: key,
              value: values?.[key],
            }))}
          />
        )}
      </div>
      <DataTable
        footer={
          user?.getAdmin
            ? () => null
            : () => (
              <Text c="red" fw={700}>
                You don't have permission to view this page
              </Text>
            )
        }
        columns={columns}
        dataSource={data}
        loading={isLoading}
        scroll={{ x: 500 }}
      />
    </div>
  );
};

export default Admin;

const AddData = ({ refech, values, update2 }) => {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [permissions, setPermissions] = useState(() =>
    Object.fromEntries(values.map((v) => [v.key, false]))
  );
  const [update, setUpdate] = useState(null);

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: async (vals) =>
      update ? await updateAdmin(vals, update._id) : await addAdmin(vals),
    onSuccess: () => {
      notifications.show({ message: update ? "Updated" : "Added", color: "green" });
      refech();
      setVisible(false);
    },
    onError: (err) => {
      notifications.show({ message: err?.response?.data?.message, color: "red" });
    },
  });

  // Initialise form state each time the modal opens so that:
  // 1. Stale user edits are discarded when re-opening.
  // 2. Background query refetches (which give update2 a new reference) do NOT
  //    silently reset switches while the modal is already open.
  useEffect(() => {
    if (visible) {
      if (update2) {
        setUpdate(update2);
        setEmail(update2.email || "");
        setPermissions(Object.fromEntries(values.map((v) => [v.key, update2[v.key] || false])));
      } else {
        setUpdate(null);
        setEmail("");
        setPassword("");
        setPermissions(Object.fromEntries(values.map((v) => [v.key, false])));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const allSelected = values.every((v) => permissions[v.key]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email) return notifications.show({ message: "Email is required", color: "red" });
    if (!update && !password) return notifications.show({ message: "Password is required", color: "red" });
    const vals = { email, ...permissions };
    if (!update) vals.password = password;
    mutate(vals);
  };

  return (
    <div>
      <Button onClick={() => setVisible(true)}>
        {update ? "Update" : "Add"}
      </Button>
      <Modal
        title={update ? "Update Admin" : "Add Admin"}
        opened={visible}
        onClose={() => setVisible(false)}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <TextInput
            label="Email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@example.com"
          />
          {!update && (
            <PasswordInput
              label="Password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
            />
          )}

          <Divider />

          <div className="flex items-center justify-between">
            <Title order={5}>Permissions</Title>
            <Switch
              label={allSelected ? "All" : "None"}
              checked={allSelected}
              onChange={(e) => {
                const checked = e.currentTarget.checked;
                setPermissions(Object.fromEntries(values.map((v) => [v.key, checked])));
              }}
            />
          </div>

          <SimpleGrid cols={2} spacing="sm">
            {values.map((value) => (
              <Paper withBorder p="sm" key={value.key}>
                <Switch
                  label={value.value}
                  checked={permissions[value.key] || false}
                  onChange={(e) => {
                    const checked = e.currentTarget.checked;
                    setPermissions((prev) => ({ ...prev, [value.key]: checked }));
                  }}
                />
              </Paper>
            ))}
          </SimpleGrid>

          <Button type="submit" loading={isLoading} mt="sm">
            Submit
          </Button>
        </form>
      </Modal>
    </div>
  );
};
